import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy } from '@angular/core'

@Directive({
  selector: '[appAutoShrinkWrappedText]',
})
export default class AutoShrinkWrappedTextDirective implements AfterViewInit, OnDestroy {
  @Input() appAutoShrinkWrappedText = '[data-auto-shrink-text]'

  private resizeObserver?: ResizeObserver
  private mutationObserver?: MutationObserver

  private rafId = -1
  private debounceTimer = -1
  private destroyed = false

  private usingWindowResizeFallback = false

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly zone: NgZone
  ) {}

  ngAfterViewInit(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return

    this.zone.runOutsideAngular(() => {
      this.installObservers()
      this.scheduleMeasure()
    })
  }

  ngOnDestroy(): void {
    this.destroyed = true

    if (this.rafId !== -1) {
      cancelAnimationFrame(this.rafId)
      this.rafId = -1
    }

    if (this.debounceTimer !== -1) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = -1
    }

    this.resizeObserver?.disconnect()
    this.mutationObserver?.disconnect()

    if (this.usingWindowResizeFallback) {
      window.removeEventListener('resize', this.onWindowResize)
      this.usingWindowResizeFallback = false
    }
  }

  private installObservers(): void {
    const host = this.elementRef.nativeElement
    const parent = host.parentElement

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.scheduleMeasure()
      })
      if (parent) {
        this.resizeObserver.observe(parent)
      }
      this.resizeObserver.observe(host)
    } else {
      this.usingWindowResizeFallback = true
      window.addEventListener('resize', this.onWindowResize, { passive: true })
    }

    const textEl = this.getTextElement()
    const mutationTarget = textEl ?? host

    if (typeof MutationObserver !== 'undefined') {
      this.mutationObserver = new MutationObserver(() => {
        this.scheduleMeasureDebounced()
      })

      this.mutationObserver.observe(mutationTarget, {
        characterData: true,
        childList: true,
        subtree: true,
      })
    }
  }

  private readonly onWindowResize = () => {
    this.scheduleMeasure()
  }

  private scheduleMeasure(): void {
    if (this.destroyed) return

    if (this.rafId !== -1) {
      cancelAnimationFrame(this.rafId)
    }

    this.rafId = requestAnimationFrame(() => {
      this.rafId = -1
      this.measureAndApply()
    })
  }

  private scheduleMeasureDebounced(): void {
    if (this.destroyed) return

    if (this.debounceTimer !== -1) {
      clearTimeout(this.debounceTimer)
    }

    this.debounceTimer = window.setTimeout(() => {
      this.debounceTimer = -1
      this.scheduleMeasure()
    }, 16) // ~1 frame debounce
  }

  private getTextElement(): HTMLElement | null {
    const host = this.elementRef.nativeElement
    const selector = this.appAutoShrinkWrappedText || '[data-auto-shrink-text]'
    return host.querySelector(selector)
  }

  private clearWrappedState(host: HTMLElement): void {
    host.classList.remove('is-wrapped')
    host.style.width = ''
    host.style.marginLeft = ''
    host.style.marginRight = ''
  }

  private measureAndApply(): void {
    if (this.destroyed) return

    const host = this.elementRef.nativeElement
    const parent = host.parentElement

    if (!parent) {
      this.clearWrappedState(host)
      return
    }

    const maxAllowed = parent.clientWidth
    if (maxAllowed <= 0) {
      this.clearWrappedState(host)
      return
    }

    const textEl = this.getTextElement()
    if (!textEl) {
      this.clearWrappedState(host)
      return
    }

    host.style.width = '100%'

    const hostWidth = host.getBoundingClientRect().width
    if (hostWidth <= 0) {
      this.clearWrappedState(host)
      return
    }

    const range = document.createRange()
    range.selectNodeContents(textEl)
    const rects = Array.from(range.getClientRects())

    if (!rects.length) {
      this.clearWrappedState(host)
      return
    }

    host.classList.toggle('is-wrapped', rects.length > 1)

    const maxLineWidth = rects.reduce((max, r) => Math.max(max, r.width), 0)

    const iconEl = host.querySelector('img, svg') as HTMLElement | null
    const iconWidth = iconEl ? iconEl.getBoundingClientRect().width : 0

    const styles = getComputedStyle(host)
    const paddingLeft = parseFloat(styles.paddingLeft) || 0
    const paddingRight = parseFloat(styles.paddingRight) || 0

    const gapRaw = styles.gap || styles.columnGap || '0'
    const gap = parseFloat(gapRaw) || 0

    const gapToAdd = iconEl ? gap : 0
    const targetWidth = Math.ceil(paddingLeft + paddingRight + iconWidth + gapToAdd + maxLineWidth)
    const clamped = Math.min(targetWidth, maxAllowed)

    host.style.width = `${clamped}px`
    host.style.marginLeft = 'auto'
    host.style.marginRight = 'auto'
  }
}
